import uniq from 'lodash/uniq';
import { get, patch, post, put } from '../../utils/api';
import {
  CUSTOMER_PREFIX,
  MESSAGE_LIMIT,
  NEW_CONV_SUFFIX,
  PROSPECT_PREFIX,
} from './constants';
import {
  customerToHouseholdMember,
  prospectToHouseholdMember,
  messageDtoToMessage,
  conversationDtoToConversation,
} from './mappings';
import type { HouseholdMember } from './types';

export function fetchHouseholdMembers({
  prospectId,
  householdId,
  selectedProperty,
}): Promise<HouseholdMember[]> {
  const orgPropPrefix = `/${selectedProperty.organizationId}/${selectedProperty.id}/`;

  if (prospectId) {
    return get(`${orgPropPrefix}prospect/${prospectId}`).then((response) => {
      return [prospectToHouseholdMember(response)];
    });
  }

  return get(
    `${orgPropPrefix}households/${householdId}/customers/texting-customers`,
  ).then((response) => {
    return response.map(customerToHouseholdMember);
  });
}

export function fetchCommunicationPreferences({ contactId }) {
  return get(`/contacts/${contactId}/communication-preferences`);
}

/**
 * A lead that has not applied yet has no household, so the backend exposes the same Leslie
 * config under a prospect-addressed route. Which one we hit is decided by whichever id the
 * profile handed us — PeopleProfile only knows the prospect, ApplicationProfile only the
 * household.
 */
function leslieConfigPath({ prospectId, householdId, selectedProperty }) {
  const orgPropPrefix = `/${selectedProperty.organizationId}/${selectedProperty.id}/`;

  return prospectId
    ? `${orgPropPrefix}prospects/${prospectId}/leslie-config`
    : `${orgPropPrefix}households/${householdId}/leslie-config`;
}

export function fetchLeslieConfig({
  prospectId,
  householdId,
  selectedProperty,
}) {
  return get(leslieConfigPath({ prospectId, householdId, selectedProperty }));
}

export function updateLeslieConfig({
  prospectId,
  householdId,
  selectedProperty,
  enabled,
}) {
  return put(
    leslieConfigPath({ prospectId, householdId, selectedProperty }),
    JSON.stringify({ enabled }),
  );
}

export async function sendMessage({ message, conversation }) {
  const recipientId = conversation.recipients[0].id;
  const isProspect = recipientId.startsWith(PROSPECT_PREFIX);
  const recipientKey = isProspect ? 'prospectId' : 'customerId';
  const recipientValue = isProspect
    ? recipientId.replace(PROSPECT_PREFIX, '')
    : recipientId.replace(CUSTOMER_PREFIX, '');

  const response = await post(
    `${import.meta.env.REACT_APP_TWO_WAY_API_URL}/messages`,
    JSON.stringify({
      content: message,
      recipient: {
        [recipientKey]: recipientValue,
      },
    }),
    // The full body carries the consent-block code that useSendMessage branches on.
    { isOtherApi: true, fullError: true },
  );

  return response;
}

export async function fetchConversations({ householdMembers, leslieEnabled }) {
  if (!householdMembers || householdMembers.length === 0) return [];

  const recipientIdParam = householdMembers
    .map((hm) => {
      const isProspect = hm.id.startsWith(PROSPECT_PREFIX);
      const recipientId = isProspect
        ? hm.id.replace(PROSPECT_PREFIX, '')
        : hm.id.replace(CUSTOMER_PREFIX, '');
      return `recipientId=${recipientId}`;
    })
    .join('&');

  const conversations = await get(
    `${
      import.meta.env.REACT_APP_TWO_WAY_API_URL
    }/conversations?${recipientIdParam}`,
    { isOtherApi: true },
  );

  return householdMembers.map((hm) => {
    const conversation = conversations.find((c) => {
      const recipient = c.recipients[0];
      return (
        hm.id.includes(recipient.customerId) ||
        hm.id.includes(recipient.prospectId)
      );
    });

    return conversationDtoToConversation(
      conversation ?? {
        _id: `${hm.id}${NEW_CONV_SUFFIX}`,
        lastReceivedMessage: null,
      },
      hm,
      leslieEnabled,
    );
  });
}

export async function fetchMessages({
  conversation,
  selectedProperty,
  cursor,
  includeUser,
  includeAttachments,
  leslieEnabled,
}) {
  if (!conversation || conversation.id.includes(NEW_CONV_SUFFIX)) return [];

  const messages = await get(
    `${import.meta.env.REACT_APP_TWO_WAY_API_URL}/messages?conversationId=${
      conversation.id
    }&limit=${MESSAGE_LIMIT}${cursor ? `&cursor=${cursor}` : ''}`,
    { isOtherApi: true },
  );

  const messagesArray = Array.isArray(messages) ? messages : [];

  const messagesWithUser = messagesArray.filter(
    (m) => m.userId && !m.isAutomated,
  );
  const userIds = uniq(messagesWithUser.map((m) => m.userId));

  const users =
    includeUser && userIds.length
      ? await get(`/users?id=${userIds.join(',')}`)
      : [];

  return messagesArray.map((m) =>
    messageDtoToMessage({
      messageDto: m,
      conversation,
      selectedProperty,
      includeUser,
      user: users.find((u) => u.id === m.userId),
      includeAttachments,
      leslieEnabled,
    }),
  );
}

/**
 * Clears the Leslie flag on one message, once the reply answering it has been sent.
 *
 * Deliberately a separate call from the send rather than a field on it: the message has to
 * exist before its flag can be cleared, and the two live in different collections with no
 * transaction between them. Sending and failing to resolve leaves the prospect answered and the
 * flag still up, which is recoverable; resolving and failing to send would not be.
 *
 * No actor is passed. The route authenticates with the user's own JWT, and two-way stamps the
 * resolver from that — a body-supplied id is only honoured for service tokens, so sending one
 * here would be ignored at best.
 */
export async function resolveMessageNeedsHelp({ messageId }) {
  return patch(
    `${import.meta.env.REACT_APP_TWO_WAY_API_URL}/messages/needs-help`,
    JSON.stringify({ messageId, isFlagged: false }),
    { isOtherApi: true },
  );
}

export async function updateConversation(conversationId: string, body: Object) {
  return put(
    `${
      import.meta.env.REACT_APP_TWO_WAY_API_URL
    }/conversations/${conversationId}`,
    JSON.stringify(body),
    { isOtherApi: true },
  );
}
